import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReactComponent as CloseSmallWhiteIcon } from '@assets/icons/close-small-white.svg';

import Editor from '@components/editor/Editor';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import CommentReplyPanel from '@features/designTool/components/CommentReplyPanel';
import PostReplyList from '@features/designTool/components/PostReplyList';
import { SelectedFileViewComponent } from '@features/task/components/TaskManageComponents/SelectedFileViewComponent';

import { clearCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearAllChats } from '@store/actions/ChatAction';
import { fetchDesignToolComment } from '@store/actions/DesignToolAction';

import ApiClient from '@lib/apiClient';

import {
	getMinsCountFromNow,
	getTimeCount,
	getUserInfo,
	onGetLocalFromUtc,
	parseAndRenderLinks
} from '@utils/Util';

const DesignToolRightSideBar = ({ productId, selectedPostId, brandName = 'BRAND' }) => {
	const designToolCommentStore = useSelector((store) => store.designToolCommentStore);
	const [addComment, setAddComment] = useState(false);
	const [loader, setLoader] = useState(true);
	const [commentError, setCommentError] = useState('');
	const [selectedFiles, setSelectedFiles] = useState([]);
	const dispatch = useDispatch();
	const { id: paramsId } = useParams();
	const id = productId || paramsId;
	const postInputRef = useRef(null);
	const chatInfo = useRef(null);
	const searchRef = useRef();
	const dropdownRef = useRef(null);
	const [, setReadOnly] = useState(true);
	const [mentionSearch, setMentionSearch] = useState('');
	const [isBuyerMentioned, setIsBuyerMentioned] = useState(false);
	const [isNitexMentioned, setIsNitexMentioned] = useState(true);
	const [mentionToUsers, setMentionToUsers] = useState([]);
	const [selectedMentionedUser, setSelectedMentionedUser] = useState([]);
	const [allComentsData, setAllCommentsData] = useState(null);
	const [selectedPostTag, setSelectedPostTag] = useState('PRODUCT_DEVELOPMENT_COMMENT');
	const isNewPost = useSelector((store) => store.chatReducer.newMessage);
	const [editorContent, setEditorContent] = useState(null);
	const [mentionIds, setMentionIds] = useState([]);

	const scrollDownInChatBody = () => {
		let container = document.getElementById('taskPostScroll');
		let o1 = container.scrollHeight - container.clientHeight;
		let elem = document.getElementById('taskPostScroll');
		elem.scrollTop = o1;
	};

	useEffect(() => {
		setAllCommentsData(designToolCommentStore?.commentList);
		scrollDownInChatBody();
	}, [designToolCommentStore?.commentList]);

	useEffect(() => {
		(async () => {
			setLoader(true);
			await dispatch(fetchDesignToolComment(id, generateParams(0)));
			setLoader(false);
		})();
	}, [id, selectedPostTag]);

	const generateParams = (page = 0) => {
		let params = `?page=${page}&size=1000&sort=id,desc`;
		if (selectedPostTag) {
			params += `&postTag=${selectedPostTag}`;
		}
		return params;
	};

	useEffect(() => {
		chatInfo.current = designToolCommentStore.commentList;
	}, [designToolCommentStore.commentList]);

	useEffect(() => {
		scrollDownInChatBody();
		const postResponse = isNewPost;
		const cloneChatInfo = JSON.parse(JSON.stringify(allComentsData));

		if (postResponse?.text && getUserInfo()?.id !== postResponse?.postedBy.id) {
			if (postResponse?.parentPostId) {
				cloneChatInfo.data.forEach((post) => {
					if (parseInt(post.id) === parseInt(postResponse?.parentPostId)) {
						post.commentList.push(postResponse);
						toastSuccess('New message received');
						setTimeout(() => {
							dispatch(clearAllChats());
						}, 1000);
					}
				});
			} else {
				cloneChatInfo.data.push(postResponse);
				cloneChatInfo.totalElements = cloneChatInfo.totalElements + 1;
			}
			setAllCommentsData(cloneChatInfo);
			if (!postResponse?.parentPostId) {
				// setMinimize(false)
				toastSuccess('New message received');
				setTimeout(() => {
					dispatch(clearAllChats());
				}, 1000);
			}
		}
	}, [isNewPost?.id]);

	const handleScroll = (e) => {
		const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
		if (bottom && chatInfo.current?.totalElements !== chatInfo.current?.data?.length) {
			setLoader(true);
			dispatch(fetchDesignToolComment(id, generateParams(chatInfo.current.currentPage + 1)));
			setLoader(false);
		}
	};

	const renderTimeCount = (item) => {
		let prev = onGetLocalFromUtc(item?.dateAdded);
		const inMins = getMinsCountFromNow(prev, 'YYYY-MM-DD HH:mm:ss');
		const timeAgo = getTimeCount(inMins, prev, true);
		return <p className="regular-12 gray_dark_02 text-uppercase">{timeAgo}</p>;
	};

	const renderComment = () => {
		return (
			allComentsData?.data?.length > 0 &&
			allComentsData?.data?.map((item, index) => {
				return (
					<div
						className={`single-comment-container ${
							item.id === selectedPostId ? 'highlight-wrapper' : ''
						}`}
						key={`design_tool_${index}`}
					>
						<div className="title d-flex">
							{item?.postPositionNo ? (
								<span className="comment-number">{item?.postPositionNo}</span>
							) : (
								<img
									src={
										item?.postedBy?.imageUrl
											? item?.postedBy?.imageUrl
											: `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
									}
									alt="profile"
									className="profile-image"
								/>
							)}

							<div className="ml-2">
								<span className="regular-14 gray_dark_01 weight-500">
									{item?.postedBy?.name}
								</span>
								{item?.artBoardName && (
									<span className="semibold-12 gray_dark_02 ml-1">
										{' '}
										on <span className="text-uppercase">{item?.artBoardName}</span>
									</span>
								)}
								{item?.isSeen === false && <span className="blue-dot ml-1"></span>}

								{renderTimeCount(item)}
							</div>
						</div>
						<div className="comments-description parent">
							<p
								dangerouslySetInnerHTML={{
									__html: parseAndRenderLinks(item?.text)
								}}
								className="description-text"
								style={{ wordWrap: 'break-word' }}
							/>
							{renderFilePreview(item.docList, false)}
							<CommentReplyPanel
								parentPost={item}
								productId={id}
								toolType={'design-tool'}
								selectedPostTag={selectedPostTag}
								generateParams={generateParams}
							/>
						</div>
						<div className="reply-list">
							<PostReplyList commentList={item.commentList} highlightReplyId={selectedPostId} />
						</div>
					</div>
				);
			})
		);
	};

	const onMultipleFileSelect = (e, docType) => {
		const files = Array.from(e.target.files);
		const fileList = [...selectedFiles];
		files.map((item) => {
			const data = {
				name: item.name,
				docMimeType: item.type,
				documentType: docType,
				print: false
			};
			const reader = new FileReader();
			reader.readAsDataURL(item);
			reader.onload = async () => {
				data.base64Str = reader.result;
				fileList.push(data);
			};
		});

		setTimeout(() => {
			setSelectedFiles(fileList);
		}, 500);
	};

	const removeFile = (index) => {
		const fileList = selectedFiles.filter((file, i) => i !== index);
		setSelectedFiles(fileList);
	};

	const renderFilePreview = (fileList, removeOption) => {
		if (fileList.length > 0) {
			return (
				<div className={'files-n-photos custom-scrollbar}'}>
					{fileList.map((file, i) => {
						return (
							<SelectedFileViewComponent
								showRemoveOption={removeOption}
								file={file}
								key={i}
								index={i}
								remove={removeFile}
							/>
						);
					})}
				</div>
			);
		}
	};

	const handleCancel = () => {
		setCommentError('');
		setAddComment(false);
		setSelectedFiles([]);
		setSelectedMentionedUser([]);
		postInputRef.current = null;
	};

	const handlePost = () => {
		let newComment = editorContent === null ? '' : editorContent.toString().replace('<p></p>', '').trim();
		if (!newComment) {
			setCommentError('Comment required');
			return;
		}
		setCommentError('');
		setLoader(true);
		let body = {
			documentDTOList: selectedFiles,
			productId: parseInt(id),
			postType: 'PRODUCT_DEVELOPMENT_COMMENT',
			postTag: selectedPostTag,
			text: editorContent,
			taggedUserIdList: mentionIds
		};
		body.recipientList = [];
		if (isNitexMentioned) body.recipientList.push({ recipientType: 'NITEX' });

		if (isBuyerMentioned && brandName !== 'Nitex') body.recipientList.push({ recipientType: 'BUYER' });

		if (selectedMentionedUser) {
			selectedMentionedUser.map((user) => {
				const userIdMap = {
					recipientType: 'INDIVIDUAL',
					userId: user.memberId
				};
				body.recipientList.push(userIdMap);
			});
		}
		if (body.recipientList.length === 0) {
			toastError('At least one mention needed');
			setIsNitexMentioned(true);
			setIsBuyerMentioned(true);
			return;
		}
		ApiClient.POST('addNewPost', body)
			.then(() => {
				setSelectedFiles([]);
				dispatch(fetchDesignToolComment(id, generateParams(0)));
				setLoader(false);
				setAddComment(false);
				handleCancel();
			})
			.catch((error) => {
				setLoader(false);
				toastError(error.response.data.message);
			});
	};

	useEffect(() => {
		document.body.addEventListener('keydown', handleKeyDown);
		return () => {
			document.body.removeEventListener('keydown', handleKeyDown);
			dispatch(clearCurrentActivePage());
		};
	}, []);

	const isQuillMentionListExist = () => {
		let quillLists = document.getElementById('quill-mention-list');
		return !!quillLists;
	};

	const handleKeyDown = (event) => {
		if (
			document.activeElement.getAttribute('class') === 'outline-none mention-search-box' ||
			document.activeElement.getAttribute('class') === 'single-item-focus'
		) {
			const isUp = event.key === 'ArrowUp';
			const isDown = event.key === 'ArrowDown';
			const inputIsFocused = document.activeElement === searchRef.current;
			const resultsItems = Array.from((dropdownRef.current && dropdownRef.current.children) || []);
			const activeResultIndex = resultsItems.findIndex((child) => {
				return child.querySelector('span') === document.activeElement;
			});

			if (isUp) {
				if (!isQuillMentionListExist()) {
					if (inputIsFocused) {
						resultsItems[resultsItems.length - 1].querySelector('span').focus();
					} else if (resultsItems[activeResultIndex - 1]) {
						resultsItems[activeResultIndex - 1].querySelector('span').focus();
					} else {
						searchRef.current && searchRef.current.focus();
					}
				}
			}

			if (isDown) {
				if (!isQuillMentionListExist()) {
					if (inputIsFocused) {
						resultsItems[0].querySelector('span').focus();
					} else if (resultsItems[activeResultIndex + 1]) {
						resultsItems[activeResultIndex + 1].querySelector('span').focus();
					} else {
						searchRef.current && searchRef.current.focus();
					}
				}
			}
		}
	};

	const selectBuyer = () => {
		setIsBuyerMentioned(true);
		setMentionToUsers([]);
		setMentionSearch('');
	};

	const selectNitex = () => {
		setIsNitexMentioned(true);
		setMentionToUsers([]);
		setMentionSearch('');
	};

	const handleMentionTo = async (e) => {
		setMentionSearch(e.target.value);
		if (e.target.value) {
			const data = designToolCommentStore?.memberList?.filter(
				(member) => member.email.toUpperCase().indexOf(e.target.value.toUpperCase()) !== -1
			);
			setMentionToUsers(data);
		} else {
			setMentionToUsers([]);
		}
	};

	const selectedUserToMention = (value) => {
		const selectedUser = mentionToUsers?.filter((el) => el.email === value);
		if (selectedUser?.length > 0) {
			let tmpArray = [...selectedMentionedUser];
			tmpArray.push(selectedUser[0]);
			setSelectedMentionedUser(tmpArray);
			setMentionSearch('');
			setMentionToUsers([]);
		}
	};

	const onDismissSelection = (userId) => {
		const userDismiss = [...selectedMentionedUser];
		const index = selectedMentionedUser.findIndex((el) => el.id === userId);
		userDismiss.splice(index, 1);
		setSelectedMentionedUser(userDismiss);
	};

	const renderUserMention = () => {
		if (addComment) {
			return (
				<div className="tag-persons d-flex flex-wrap" onClick={() => setReadOnly(true)}>
					<span className="mr-2">To</span>
					<div className="d-flex gap-2 flex-wrap">
						{isNitexMentioned && (
							<div className="flex items-center gap-2">
								<div className="tag-badge normal-case">
									<span>Nitex</span>
								</div>
							</div>
						)}
						{isBuyerMentioned && (
							<div className="d-flex flex-wrap items-center gap-2">
								<div className="tag-badge normal-case">
									<span>{brandName}</span>
									<span
										className="ml-1 cursor-pointer"
										onClick={() => setIsBuyerMentioned(false)}
									>
										<CloseSmallWhiteIcon />
									</span>
								</div>
							</div>
						)}
						{selectedMentionedUser &&
							selectedMentionedUser.map((user) => {
								return (
									<div key={user.id} className="d-flex items-center gap-2">
										<div className="tag-badge normal-case ">
											<span>{user.email}</span>
											<span
												className="ml-1 cursor-pointer"
												onClick={() => onDismissSelection(user.id)}
											>
												<CloseSmallWhiteIcon />
											</span>
										</div>
									</div>
								);
							})}
					</div>
					<div className="position-relative ml-2" onClick={() => setReadOnly(true)}>
						<div className="suggetions bottom-full bg-white shadow-lg scroll-y-label">
							<ul
								className="max-h-[300px] custom-scroll overflow-y-auto pr-2"
								ref={dropdownRef}
							>
								{!isNitexMentioned && mentionSearch !== '' && (
									<li
										key={'nitex'}
										className="cursor-pointer px-3 py-1"
										onClick={selectNitex}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												selectNitex();
											}
										}}
									>
										<span className="single-item-focus" tabIndex={0}>
											Nitex
										</span>
									</li>
								)}
								{!isBuyerMentioned && mentionSearch !== '' && (
									<li
										key={'buyer'}
										className="cursor-pointer px-3 py-1"
										onClick={selectBuyer}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												selectBuyer();
											}
										}}
									>
										<span className="single-item-focus" tabIndex={0}>
											{brandName}
										</span>
									</li>
								)}
								{mentionToUsers?.map((user) => {
									return (
										<li
											key={user.id}
											className="cursor-pointer px-3 py-1"
											onClick={() => selectedUserToMention(user.email)}
											onKeyPress={(e) => {
												if (e.key === 'Enter') {
													selectedUserToMention(user.email);
												}
											}}
										>
											<span className="single-item-focus" tabIndex={0}>
												{user.email}
											</span>
										</li>
									);
								})}
							</ul>
						</div>
						<input
							type="text"
							autoComplete="off"
							className="outline-none mention-search-box"
							placeholder="Search with email"
							value={mentionSearch}
							onChange={handleMentionTo}
							onSelect={selectedUserToMention}
							ref={searchRef}
						/>
					</div>
				</div>
			);
		}
	};
	return (
		<LoaderComponent loading={loader}>
			<div className="design-tool-sidebar comments-sidebar">
				<div className="design-tool-comments">
					<div className="comments-heading">
						<p className="regular-12 gray_dark_02 mb-0">COMMENTS</p>
					</div>
					<div className="conversation-filter-dropdown bg-white">
						<select
							className="w-100 mb-12"
							name="postTag"
							id="postTag"
							value={selectedPostTag}
							onChange={(e) => {
								setSelectedPostTag(e.target.value);
							}}
						>
							<option value={''}>All</option>
							<option value="PRODUCT_DEVELOPMENT_COMMENT">Design</option>
							<option value="PRODUCT_SAMPLE_COMMENT">Sample</option>
							<option value="PRODUCT_QUOTE_COMMENT">Costing</option>
						</select>
					</div>

					<div
						className={`all-comments-body custom-scrollbar ${addComment ? 'add-comments-footer-active' : ''}`}
						onScroll={handleScroll}
						id="taskPostScroll"
					>
						{allComentsData?.data?.length === 0 && !loader ? (
							<div className="no-comments">No comment found</div>
						) : (
							renderComment()
						)}
					</div>

					<div className="sidebar-comments-footer">
						<div className={addComment ? 'comments-container expand' : 'comments-container'}>
							{renderUserMention()}
							<div className="artboard-comment-box techpack-editor">
								{getUserInfo()?.profilePicDocument?.docUrl && (
									<img
										src={getUserInfo()?.profilePicDocument?.docUrl}
										alt="profile"
										className="profile"
									/>
								)}
								{!getUserInfo()?.profilePicDocument?.docUrl && (
									<img
										src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
										alt="profile"
										className="profile"
									/>
								)}
								{!addComment ? (
									<textarea
										placeholder="Write comment..."
										onClick={() => {
											setAddComment(true);
										}}
									/>
								) : (
									<Editor
										setEditorContent={setEditorContent}
										setMentionIds={setMentionIds}
										memberList={designToolCommentStore?.memberList}
									/>
								)}
								{commentError && <p className="error">{commentError}</p>}
							</div>
							{renderFilePreview(selectedFiles, true)}
							{addComment && (
								<div className="artboard-comment-footer">
									<span className="upload">
										<label htmlFor="attachment">
											<img
												src={`${process.env.PUBLIC_URL}/icons/attach20.svg`}
												alt="attachment"
											/>
										</label>
										<input
											type="file"
											id="attachment"
											name="attachment"
											multiple
											onChange={(e) => onMultipleFileSelect(e, 'ACCESSORIES_DESIGN')}
										/>
									</span>
									<span className="action-buttons">
										<button onClick={handleCancel}>CANCEL</button>
										<button onClick={handlePost}>POST</button>
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default DesignToolRightSideBar;
